/**
 * ConnectionDetailPage (Phase 2, §7.5; FinTS block Phase 4 §7.3) — one
 * connection with its accounts and its scoped sync-run history. Reuses
 * `ConnectionCard` for the header/actions and `SyncHealthPanel` (scoped to
 * this connection) for the run log. For FinTS connections, also renders the
 * `fints: FintsAccessOut` block (BLZ, Login, Server, TAN-Verfahren) — never
 * the PIN or the encrypted client-data blob, which the API never returns.
 */
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { PageHeader } from '../../components/layout/PageHeader.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { ErrorState } from '../../components/ui/ErrorState.tsx';
import { SkeletonList } from '../../components/ui/Skeleton.tsx';
import { useConnection } from '../../lib/hooks/useConnections.ts';
import { ConnectionCard } from './components/ConnectionCard.tsx';
import { SyncHealthPanel } from './SyncHealthPanel.tsx';

export default function ConnectionDetailPage() {
  const navigate = useNavigate();
  const params = useParams();
  const id = Number(params.id);
  const query = useConnection(id);

  return (
    <div>
      <PageHeader
        title={query.data?.bank_label ?? 'Bankverbindung'}
        actions={
          <Button variant="secondary" size="sm" onClick={() => void navigate('/bankverbindungen')}>
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Zurück</span>
          </Button>
        }
      />

      <div className="w-full space-y-10 px-4 py-6 sm:px-6">
        {query.isPending ? (
          <SkeletonList rows={3} />
        ) : query.isError ? (
          <ErrorState
            title="Bankverbindung nicht gefunden"
            error={query.error}
            onRetry={() => void query.refetch()}
          />
        ) : (
          <>
            <ConnectionCard connection={query.data} linkToDetail={false} />
            {query.data.fints ? (
              <section>
                <h2 className="mb-4 text-xs font-semibold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500">
                  FinTS-Zugang
                </h2>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm sm:grid-cols-4">
                  <div>
                    <dt className="text-xs text-slate-400 dark:text-slate-500">BLZ</dt>
                    <dd className="text-slate-900 dark:text-slate-100">{query.data.fints.blz}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-400 dark:text-slate-500">Login</dt>
                    <dd className="truncate text-slate-900 dark:text-slate-100">
                      {query.data.fints.login_id}
                    </dd>
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <dt className="text-xs text-slate-400 dark:text-slate-500">Server</dt>
                    <dd className="truncate text-slate-900 dark:text-slate-100">
                      {query.data.fints.server_url}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-400 dark:text-slate-500">TAN-Verfahren</dt>
                    <dd className="text-slate-900 dark:text-slate-100">
                      {query.data.fints.tan_mechanism ?? '—'}
                    </dd>
                  </div>
                </dl>
              </section>
            ) : null}
            <section>
              <h2 className="mb-4 text-xs font-semibold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500">
                Abgleich-Verlauf
              </h2>
              <SyncHealthPanel connectionId={id} limit={20} />
            </section>
          </>
        )}
      </div>
    </div>
  );
}
